/**
 * 吸顶的一叠：页头 → 对象页签 → 表头（样式包 console.css §2、DESIGN.md §4.7）。样子全在样式包里，
 * 这里照 console.js 的判断写 React 版（技术栈契约「样式与组件」：不引 console.js，按它的行为自己实现）：
 * - 表头算「停住」：表格的上沿已经推到表头上面去了、表格还没整个滚出去，加 .is-stuck；
 * - 表头最多停到最后一行的上沿：再往上推，表头贴着最后一行一起走，往上让的距离写进 --th-shift，并加 .is-leaving。
 * 表格是 React 画的，className 固定是 "tbl"，React 不会去动这两个类；表格重新挂载后下一次滚动或 DOM 变化时补上。
 */
import { useEffect } from "react";

function updateTables() {
  document.querySelectorAll<HTMLTableElement>(".main .tbl").forEach((t) => {
    if (!t.tHead || t.closest("dialog, .pop")) return;
    const th = t.tHead.rows[0]?.cells[0];
    const sticky = !!th && !t.closest("[hidden]") && getComputedStyle(th).position === "sticky";
    const old = parseFloat(t.style.getPropertyValue("--th-shift")) || 0;
    // 不让的时候表头停在哪
    const line = sticky && th ? parseFloat(getComputedStyle(th).top) + old : 0;
    const rows = t.tBodies[0]?.rows;
    const last = rows && rows.length ? rows[rows.length - 1] : null;
    const shift = sticky && th && last ? Math.max(0, line + th.offsetHeight - last.getBoundingClientRect().top) : 0;
    if (Math.abs(shift - old) > 0.1) t.style.setProperty("--th-shift", `${shift}px`);
    const r = t.getBoundingClientRect();
    t.classList.toggle("is-stuck", sticky && r.top < line - 0.5 && r.bottom > line);
    t.classList.toggle("is-leaving", shift > 0);
  });
}

/** 在外壳上挂一次：滚动、窗口变化、页面内容变化（表格取完数才出现）时都重新判断 */
export function useStickyTables() {
  useEffect(() => {
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        updateTables();
      });
    };
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    const main = document.querySelector(".main");
    const mo = main ? new MutationObserver(schedule) : null;
    if (main && mo) mo.observe(main, { childList: true, subtree: true });
    schedule();
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      mo?.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
}

/** 页头的实际高写进 --bar-now：下面吸顶的页签、表头停在它下面（数据分析页筛选换行时页头会变高） */
export function useBarHeight(bar: HTMLElement | null) {
  useEffect(() => {
    if (!bar) return;
    const root = document.documentElement;
    const measure = () => root.style.setProperty("--bar-now", `${bar.getBoundingClientRect().height}px`);
    measure();
    if (!("ResizeObserver" in window)) return;
    const ro = new ResizeObserver(measure);
    ro.observe(bar);
    return () => ro.disconnect();
  }, [bar]);
}
